import type { MeResponse } from "../api-models";

// Mirrors the server's resolution (rbac.go) for nav/menu/button gating only —
// the server is always authoritative (architecture.md §6).
//
// Scope keys take three shapes: "global", a bare site id ("7"), and "project:<id>". A
// PROJECT-scoped role contributes its project key AND a site key for every site of that
// project, so a site-keyed check needs no knowledge of projects.

const PROJECT_KEY_PREFIX = "project:";

const projectKey = (projectId: number) => `${PROJECT_KEY_PREFIX}${projectId}`;

export function hasPermission(user: MeResponse | null | undefined, perm: string, siteId?: number | null): boolean {
  if (!user) return false;
  const global = user.permissions["global"] ?? [];
  if (global.includes(perm)) return true;
  if (siteId != null) {
    const scoped = user.permissions[String(siteId)] ?? [];
    if (scoped.includes(perm)) return true;
  }
  return false;
}

// Any grant at all — global, at a site, or on a project. Every non-"global" key counts,
// which includes project keys; that is correct under additive scopes, and is what keeps a
// holder on a project with no sites yet from losing access to reference data.
export function hasAnyGrant(user: MeResponse | null | undefined, perm: string): boolean {
  if (!user) return false;
  if ((user.permissions["global"] ?? []).includes(perm)) return true;
  return Object.entries(user.permissions).some(([key, perms]) => key !== "global" && perms.includes(perm));
}

export function hasAnyPrefix(user: MeResponse | null | undefined, prefixes: string[]): boolean {
  if (!user) return false;
  for (const perms of Object.values(user.permissions)) {
    if (perms.some((p) => prefixes.some((prefix) => p.startsWith(prefix)))) return true;
  }
  return false;
}

// hasProjectGrant mirrors the server's hasWriteOnProject (rbac.go): authority over a
// project-level document that carries no site of its own — a BOM version, a
// project-level PR. Only a global grant or a genuine PROJECT-scoped grant qualifies; a
// grant at one site of the project does not.
export function hasProjectGrant(user: MeResponse | null | undefined, perm: string, projectId: number): boolean {
  if (!user) return false;
  return (
    (user.permissions["global"] ?? []).includes(perm) ||
    (user.permissions[projectKey(projectId)] ?? []).includes(perm)
  );
}

// hasGrantInProject answers a different question: "could the user do this somewhere inside
// this project?" — used to show a project tab whose actions the server then checks per
// site. Site-only permissions such as Attendance.Write (§31.27) can never appear under a
// project key, so they must be looked for under the project's own site keys.
export function hasGrantInProject(
  user: MeResponse | null | undefined,
  perm: string,
  projectId: number,
  projectSiteIds: number[],
): boolean {
  if (!user) return false;
  if (hasProjectGrant(user, perm, projectId)) return true;
  return projectSiteIds.some((siteId) => (user.permissions[String(siteId)] ?? []).includes(perm));
}

export function isModuleEnabled(user: MeResponse | null | undefined, moduleId: string): boolean {
  return !!user?.modules.some((m) => m.id === moduleId && m.enabled);
}
